import React, { Component } from 'react';
import { ThemeContext } from './components/Core/ThemeContext';
import ColorFolder from './components/Core/ColorFolder';
import PlanOutline from './components/Outlines/PlanOutline';
import { CommentHeader, CommentText, LargeGutter } from './components/Core/TextComponents';
import * as importedPlans from './plans/plans.json';
import { trackCustomEvent } from './utilities/analytics';
import Devotionals from './plans/Devotionals';
import { Dialog, DialogActions, TextField, DialogTitle, DialogContent, DialogContentText, Button } from '@material-ui/core';
import SEO from './components/SEO';
import { truncateSeoText } from './utilities/seo';
import { buildCanonicalUrl, deriveLegacyCanonicalPath } from './utilities/canonicalUrls';
import { buildPlansPath, resolvePlanName } from './utilities/planPaths';

const plansPageStyles = {
    page: {
        minHeight: '100vh',
        padding: 'clamp(8px, 1.5vw, 16px)',
        background: 'linear-gradient(180deg, #f8f8f4 0%, #edf4f3 42%, #f9fbfb 100%)',
    },
    frame: {
        width: 'min(100%, 940px)',
        margin: '0 auto',
    },
    hero: {
        display: 'flex',
        position: 'relative',
        width: 'calc(93% + 2px)',
        boxSizing: 'border-box',
        marginLeft: 'auto',
        marginRight: 'auto',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 14px 32px rgba(86, 96, 110, 0.1)',
        marginBottom: 12,
    },
    heroStrip: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: 2,
        minWidth: 2,
        zIndex: 2,
    },
    heroBody: {
        position: 'relative',
        zIndex: 1,
        flex: 1,
        padding: 'clamp(12px, 2vw, 16px)',
        paddingLeft: 'calc(clamp(12px, 2vw, 16px) + 2px)',
    },
    eyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    title: {
        margin: '4px 0 0 0',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
        fontSize: 'clamp(1.5em, 3vw, 1.9em)',
        lineHeight: 1.05,
    },
    summary: {
        margin: '6px 0 0 0',
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '1em',
        lineHeight: 1.4,
        maxWidth: 'none',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    listItem: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
};

class Plans extends Component {
    static contextType = ThemeContext;

    getDevotional()
    {
        const pathMatch = window.location.pathname.match(/^\/plans\/(.+)\/?$/);
        if (pathMatch && pathMatch[1] && !['verify', 'unsubscribe', 'goback'].includes(pathMatch[1])) {
            return resolvePlanName(pathMatch[1]);
        }
        const searchParams = new URLSearchParams(window.location.search);
        return resolvePlanName(searchParams.get('dev'));
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    constructor() {
        super();

        this.state = {
            plans: {},
            open: false,
            email: '',
            selectedDevotional: '',
        };
    }

    ensurePlanFormatted(planName) {
        if (!planName) return;
        if (this.state.plans[planName]) return;

        setTimeout(() => {
            const formattedPlan = Devotionals.getFormattedPlan(planName);
            if (!formattedPlan) return;
            this.setState((prevState) => ({
                plans: {
                    ...prevState.plans,
                    [planName]: formattedPlan,
                }
            }));
        }, 0);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.context.loading(false);

        if (window.web_version) {
            const normalizedPath = deriveLegacyCanonicalPath(window.location.pathname, window.location.search);
            if (`${window.location.pathname}${window.location.search}` !== normalizedPath) {
                window.history.replaceState({}, '', normalizedPath);
            }
        }

        const filter = this.getDevotional();
        if (filter && filter !== 'null') this.ensurePlanFormatted(filter);
    }

    async subscribe() {
        trackCustomEvent({
            ev: 'subscribe.devotional',
            unique: false,
        })

        // 1. get data and set endpoint
        const email = (this.state.email);
        const devotional = (this.state.selectedDevotional);

        const { token } = localStorage;
        this.context.loading(true);
        let endpoint = `/v1/devotionals/subscribe`;
        if (process.env.REACT_APP_AWS_API)
        {
            endpoint = window.API_BASE + endpoint;
        }
        else
        {
            endpoint = "http://localhost:5001" + endpoint;
        }

        // 2. send email + devotional id to server
        console.log('endpoint', endpoint);
        console.log('email', email);
        console.log('devotional', devotional);

        if (endpoint !== '') {
            await fetch(endpoint, {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    devotional
                }),
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            })
                .then((res) => res.json())
                .then((response) => {
                    // console.log("Response: " + new Date().getSeconds());
                    console.log("response: " + response.subscriptionCode);
                    alert('A email verification message has been sent to your email address, please check your spam folder if it does not arrive within 5 minutes.');
                }).catch((e)=>{
                    console.log('exception: ', e);
                    alert('error contacting to server', e);
                });
        }

        this.setState({open: false});
    }

    handleEmailChange(e) {
        this.setState({
            email: e.target.value
        });
    }

    render() {
        let filter = this.props.filter || this.getDevotional();
        if (filter == 'null') filter = null;

        const rawPlans = importedPlans;
        const formattedPlans = this.state.plans;
        const keys = Object.keys(rawPlans).filter(x => x !== 'default');
        const firstPlan = filter ? rawPlans[filter] : rawPlans[keys[0]];
        const heroColorIndex = (firstPlan && typeof firstPlan.colorIndex === 'number')
            ? firstPlan.colorIndex
            : 1;
        const selectedPlan = filter ? rawPlans[filter] : null;
        const seoTitle = selectedPlan
            ? `${filter} Devotional Plan | ai-BIBLE`
            : 'Bible Devotional Plans | ai-BIBLE';
        const seoDescription = selectedPlan
            ? truncateSeoText(selectedPlan.summary || `Read the ${filter} devotional plan on ai-BIBLE.`)
            : 'Read Bible devotional plans with linked passages, commentary, application, and meditation on ai-BIBLE.';
        const canonical = buildCanonicalUrl(filter ? buildPlansPath(filter) : '/plans');

        // keys.forEach(x => {
        //     console.log('dev=', encodeURIComponent(x))
        // })

        // console.log('filter [' + filter + ']')

        return (
            <>
                <SEO canonical={canonical} title={seoTitle} description={seoDescription} type="article" />
                <Dialog open={this.state.open} onClose={()=>{console.log('closed')}} aria-labelledby="form-dialog-title">
                    <DialogTitle id="form-dialog-title">Subscribe</DialogTitle>
                    <DialogContent>
                    <DialogContentText>
                        To subscribe to this devotional, please enter your email address here. We will send a verification to your email for you to accept.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Email Address"
                        type="email"
                        fullWidth
                        value={this.state.email}
                        onChange={(e)=>{this.handleEmailChange(e)}}
                    />
                    </DialogContent>
                    <DialogActions>
                    <Button onClick={()=>{this.setState({open: false})}} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={async (e)=>{
                        await this.subscribe(e, this.state.selectedDevotional);
                    }} color="primary">
                        Subscribe
                    </Button>
                    </DialogActions>
                </Dialog>
 
                <div style={plansPageStyles.page}>
                    <div style={plansPageStyles.frame}>
                        <header style={plansPageStyles.hero}>
                            <div
                                className={`background-grad-${heroColorIndex}`}
                                style={plansPageStyles.heroStrip}
                                aria-hidden="true"
                            />
                            <div style={plansPageStyles.heroBody}>
                                <p style={plansPageStyles.eyebrow}>Bible plans</p>
                                <h1 style={plansPageStyles.title}>Devotional plans</h1>
                                <p style={plansPageStyles.summary}>
                                    <strong>Bible plans generated with AI.</strong>
                                    {' '}Explore a plan below to read its summary, stages, and reflection questions.
                                </p>
                            </div>
                        </header>

                        <ol style={plansPageStyles.list}>
                    {keys.map((x, index) => {
                        const formattedPlan = formattedPlans[x];
                        const rawPlan = rawPlans[x];
                        return (((filter == x)||(!filter))?
                        <li key={index} style={plansPageStyles.listItem}>
                            <ColorFolder
                                title={x}
                                href={buildPlansPath(x)}
                                colorIndex={(rawPlan && typeof rawPlan.colorIndex === 'number') ? rawPlan.colorIndex : 0}
                                startsOpen={filter === x}
                                actionLabel={'send daily'}
                                action={(e)=>{
                                this.setState({open: true});
                                this.setState({selectedDevotional: x})
                            }} onToggle={(expanded)=>{
                                const nextPath = expanded ? buildPlansPath(x) : '/plans';
                                if (window.location.pathname !== nextPath) {
                                    window.history.replaceState({}, '', nextPath);
                                    window.dispatchEvent(new PopStateEvent('popstate'));
                                }
                                if (expanded) this.ensurePlanFormatted(x);
                            }}>
                                <CommentHeader>Summary:&nbsp;</CommentHeader>
                                <CommentText>{(formattedPlan && formattedPlan.summary) || (rawPlan && rawPlan.summary) || ''}</CommentText>

                                <LargeGutter></LargeGutter>

                                <CommentHeader>Stages </CommentHeader>
                                {(formattedPlan && formattedPlan.stages || []).map((stage, index) => (
                                    <PlanOutline key={index} stage={stage} outline={stage.passages} title={stage.title} showBookName colorIndex={2} />
                                ))}

                                <LargeGutter></LargeGutter>

                                {formattedPlan && formattedPlan.questions && (
                                    <>
                                        <CommentHeader>
                                            <strong style={{ fontWeight: 'bold' }}>Questions</strong>
                                        </CommentHeader>
                                        <ol>
                                            {formattedPlan.questions.map((question, index) => (
                                                <li key={index}>
                                                    <CommentText>{question}</CommentText>
                                                </li>
                                            ))}
                                        </ol>
                                    </>
                                )}
                            </ColorFolder>
                        </li>:null
                    )})}
                        </ol>
                    </div>
                    <div style={{ minHeight: 60 }} />
                </div>
            </>
        );
    }
}

export default Plans;
